/**
 * Feb-2026 Iter 28 (E) — Social-Media-Caption-Generator.
 *
 * Modal das auf Wunsch IG/FB/TikTok-Captions + Hashtags aus den aktuellen
 * Inseratsdaten generiert. Endpoint: POST /api/listings/ai-social-captions
 *
 * Aufruf von außen via <SocialCaptionsButton form={form} /> in der
 * „Werbung & Reichweite"-Sektion (FooterActionsSection).
 */
import { useState } from "react";
import { Instagram, Facebook, Music2, Hash, Copy, Sparkles, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const PLATFORMS = [
    { key: "instagram", label: "Instagram", Icon: Instagram, accent: "#E1306C" },
    { key: "facebook",  label: "Facebook",  Icon: Facebook,  accent: "#1877F2" },
    { key: "tiktok",    label: "TikTok",    Icon: Music2,    accent: "#111827" },
];

export default function SocialCaptionsButton({ form }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const generate = async () => {
        if (!form.brand || !form.model) {
            toast.info("Bitte zuerst Marke und Modell ausfüllen.");
            return;
        }
        setOpen(true);
        setLoading(true);
        setData(null);
        try {
            const { data: resp } = await api.post("/listings/ai-social-captions", form);
            setData(resp);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Captions konnten nicht generiert werden.");
            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const copyText = (text, label) => {
        if (!text) return;
        navigator.clipboard?.writeText(text).then(
            () => toast.success(`${label} in Zwischenablage kopiert`),
            () => toast.error("Kopieren fehlgeschlagen — bitte manuell auswählen"),
        );
    };

    return (
        <>
            <button
                type="button"
                onClick={generate}
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black"
                data-testid="social-captions-btn"
                title="KI generiert fertige Captions für Instagram, Facebook und TikTok aus deinem Inserat"
            >
                <Sparkles className="w-3 h-3" />
                KI-Social-Captions
            </button>

            {open && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setOpen(false)}
                    data-testid="social-captions-modal"
                >
                    <div
                        className="bg-white rounded-sm border-2 border-[#111827] max-w-2xl w-full max-h-[88vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-[#111827] bg-[#FFCC00]">
                            <div className="text-sm font-bold uppercase tracking-widest text-[#111827]">
                                KI-Social-Captions
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] p-1 rounded-sm"
                                data-testid="social-captions-close"
                                aria-label="Schließen"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            {loading && (
                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Generiere Captions via Gemini Flash …
                                </div>
                            )}
                            {!loading && data && (
                                <>
                                    {PLATFORMS.map(({ key, label, Icon, accent }) => (
                                        <div key={key} className="border border-gray-200 rounded-sm" data-testid={`caption-${key}`}>
                                            <div
                                                className="flex items-center justify-between px-3 py-2 border-b border-gray-200"
                                                style={{ backgroundColor: `${accent}10` }}
                                            >
                                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
                                                    <Icon className="w-4 h-4" />
                                                    {label}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => copyText(data[key], label)}
                                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                                                    data-testid={`caption-copy-${key}`}
                                                >
                                                    <Copy className="w-3 h-3" /> Kopieren
                                                </button>
                                            </div>
                                            <p className="px-3 py-2.5 text-sm text-gray-900 whitespace-pre-line leading-snug">
                                                {data[key]}
                                            </p>
                                        </div>
                                    ))}
                                    <div className="border border-gray-200 rounded-sm" data-testid="caption-hashtags">
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-100">
                                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700">
                                                <Hash className="w-4 h-4" /> Hashtags
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => copyText((data.hashtags || []).join(" "), "Hashtags")}
                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                                                data-testid="caption-copy-hashtags"
                                            >
                                                <Copy className="w-3 h-3" /> Kopieren
                                            </button>
                                        </div>
                                        <div className="px-3 py-2.5 flex flex-wrap gap-1.5">
                                            {(data.hashtags || []).map((h) => (
                                                <span key={h} className="text-[12px] font-mono text-[#111827] bg-gray-100 hover:bg-gray-200 px-1.5 py-0.5 rounded-sm cursor-default">
                                                    {h}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    {data.mocked && (
                                        <div className="text-[11px] text-gray-500 italic">
                                            Fallback-Modus aktiv — Captions sind generisch.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
